import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DefaultService, LeagueOutput } from '../generated-api';
import ImageCameraDialog from '../components/common/ImageCameraDialog';
import PlayerAvatar from '../components/common/PlayerAvatar';
import PageHeader from '../components/common/PageHeader';
import { useSession } from '../contexts/SessionContext';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useLeagues } from '../contexts/DataContext';
import { resolveLeagueIcon } from '../utils/leagueIcon';

const NewPlayerPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setSession, onboardingRequired } = useSession();
  const userInfo = location.state?.userInfo || {};

  const { fetch: fetchLeagues, invalidate: invalidateLeagues } = useLeagues();
  const [name, setName] = useState(userInfo.name || '');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [publicLeagues, setPublicLeagues] = useState<LeagueOutput[]>([]);
  const [selectedLeagueIds, setSelectedLeagueIds] = useState<Set<string>>(new Set());
  const [leaguesLoading, setLeaguesLoading] = useState(true);

  const handleImageSelected = (file: File, preview: string) => {
    setImageFile(file);
    setImagePreview(preview);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  useEffect(() => {
    const loadPublicLeagues = async () => {
      try {
        setLeaguesLoading(true);
        const allLeagues = await fetchLeagues();
        // Filter to only public leagues (isPublic === true)
        const publicLeagues = allLeagues.filter(league => league.isPublic === true);
        setPublicLeagues(publicLeagues);
        
        // Auto-select Global league (always public)
        const globalLeague = publicLeagues.find(league => league.isGlobal);
        if (globalLeague) {
          setSelectedLeagueIds(new Set([globalLeague.id]));
        }
      } catch (err) {
        console.error('Error loading public leagues:', err);
      } finally {
        setLeaguesLoading(false);
      }
    };
    
    loadPublicLeagues();
  }, [fetchLeagues]);

  const toggleLeagueSelection = (leagueId: string) => {
    setSelectedLeagueIds(prev => {
      const newSet = new Set(prev);
      // Global league should always be selected
      const globalLeague = publicLeagues.find(l => l.isGlobal);
      if (leagueId === globalLeague?.id) {
        return prev; // Don't allow deselecting Global
      }
      if (newSet.has(leagueId)) {
        newSet.delete(leagueId);
      } else {
        newSet.add(leagueId);
      }
      return newSet;
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError('Player name is required.');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      // Step 1: Create the player
      let newPlayer = await DefaultService.createPlayerPlayersPost({ requestBody: { name: name.trim() } });
      console.log('✅ Player created:', newPlayer);

      // Step 2: Upload image if provided
      if (imageFile) {
        newPlayer = await DefaultService.uploadPlayerImagePlayersPlayerIdImagePost({ playerId: newPlayer.id, formData: { image: imageFile } });
        console.log('✅ Player image uploaded');
      }

      // Step 3: Add player to selected leagues (including Global)
      if (selectedLeagueIds.size > 0) {
        const leagueIdsArray = Array.from(selectedLeagueIds);
        for (const leagueId of leagueIdsArray) {
          try {
            await DefaultService.addPlayersToLeagueLeaguesLeagueIdPlayersPost({
              leagueId,
              requestBody: { playerIds: [newPlayer.id] }
            });
            console.log(`✅ Player added to league: ${leagueId}`);
          } catch (leagueErr) {
            console.error(`⚠️ Error adding player to league ${leagueId}:`, leagueErr);
            // Continue with other leagues even if one fails
          }
        }
      }

      // Invalidate league cache since the new player was added to Global + Unranked leagues
      invalidateLeagues();

      // Step 4: Automatically claim the newly created profile if onboarding
      if (onboardingRequired) {
        console.log('🎯 Auto-claiming profile:', newPlayer.id);
        const claimedPlayer = await DefaultService.claimPlayerProfilePlayersPlayerIdClaimPost({ playerId: newPlayer.id });
        console.log('✅ Profile auto-claimed:', claimedPlayer);

        // Step 5: Get user email from auth session
        const authSession = await fetchAuthSession();
        const userEmail = authSession.tokens?.idToken?.payload?.email as string || '';

        // Step 6: Update session with claimed player
        setSession({
          onboarding_required: false,
          player_id: claimedPlayer.id,
          user_info: {
            email: userEmail,
            name: claimedPlayer.name
          }
        });
        console.log('✅ Session updated with player:', claimedPlayer.id);
      }

      // Step 7: Navigate after player creation
      // Check if we should return to a previous page (e.g., from play creation flow)
      const returnPath = location.state?.returnPath;
      if (returnPath) {
        // Navigate back to the return path (e.g., /plays/new)
        navigate(returnPath);
      } else if (onboardingRequired) {
        // For first-time users, go to home page after creating their profile
        navigate('/');
      } else {
        // For regular player creation, go to the player detail page
        navigate(`/players/${newPlayer.id}`);
      }
    } catch (err: any) {
      setError(err?.body?.detail || err.message || 'Failed to create player. Please try again.');
      console.error('❌ Error creating/claiming player:', err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader className="mb-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-heading mb-3">
            {onboardingRequired ? 'Create Your Profile' : 'Add New Player'}
          </h1>
          <p className="text-lg text-body max-w-2xl mx-auto">
            {onboardingRequired 
              ? 'This will be your identity in the game tracking system. You can track your ratings, join leagues, and compete with friends!'
              : 'Create a new player profile to add to your game tracking system.'
            }
          </p>
        </div>
      </PageHeader>

      <div className="bg-surface shadow-xl rounded-2xl border border-edge-subtle p-8 sm:p-12">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Avatar Preview Section */}
          <div className="flex flex-col items-center mb-8">
            <div className="mb-4 relative">
              <PlayerAvatar 
                name={name || 'Player'} 
                imageUrl={imagePreview || undefined} 
                size="xxlarge" 
              />
              {imagePreview && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 shadow-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                  title="Remove image"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <p className="text-sm text-muted text-center">
              {name ? `Preview: ${name}` : 'Your avatar will appear here'}
            </p>
          </div>

          {/* Player Name Input */}
          <div>
            <label htmlFor="playerName" className="block text-sm font-medium text-body mb-2">
              Player Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="playerName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 text-lg border border-edge-input rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="Enter player name"
              required
            />
            <p className="mt-2 text-sm text-muted">
              This name will be used across all games and leagues
            </p>
          </div>

          {/* Image Upload Section */}
          <div>
            <ImageCameraDialog
              onImageSelected={handleImageSelected}
              label="Profile Image (Optional)"
            />
            <p className="mt-2 text-sm text-muted">
              Add a profile picture to make your player profile more recognizable
            </p>
          </div>

          {/* League Selection Section */}
          {!leaguesLoading && publicLeagues.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-body mb-2">
                Join Leagues (Optional)
              </label>
              <p className="text-sm text-muted mb-4">
                Select public leagues to join. You'll automatically join the Global league.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {publicLeagues.map((league) => {
                  const isSelected = selectedLeagueIds.has(league.id);
                  const isGlobal = league.isGlobal;
                  
                  return (
                    <button
                      data-track="player_new_league_chip"
                      key={league.id}
                      type="button"
                      onClick={() => toggleLeagueSelection(league.id)}
                      disabled={isGlobal}
                      className={`relative flex flex-col items-center justify-center p-4 rounded-lg border transition-all ${
                        isGlobal
                          ? 'border-primary-500 bg-primary-50 cursor-not-allowed'
                          : isSelected
                          ? 'border-primary-500 bg-primary-50 hover:bg-primary-100'
                          : 'border-edge bg-surface hover:bg-surface-hover hover:border-edge-input'
                      }`}
                    >
                      <div className="text-2xl mb-1">
                        {resolveLeagueIcon(league.icon, league.isGlobal ? '🌍' : '🏆')}
                      </div>
                      <div className="text-xs font-medium text-center truncate w-full px-1">
                        {league.name}
                      </div>
                      {isGlobal && (
                        <div className="absolute top-1 right-1">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800">
                            Auto
                          </span>
                        </div>
                      )}
                      {isSelected && !isGlobal && (
                        <div className="absolute top-1 left-1">
                          <div className="w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center">
                            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-edge">
            {!onboardingRequired && (
              <button
                type="button"
                onClick={() => navigate('/players')}
                className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-body bg-surface border border-edge-input rounded-lg hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={creating || !name.trim()}
              className="w-full sm:w-auto px-8 py-3 text-base font-medium text-white bg-primary-700 border border-transparent rounded-lg hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {creating ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </span>
              ) : (
                onboardingRequired ? 'Create Profile' : 'Create Player'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewPlayerPage;